import api from '../../axios';
import { handleApiError } from '../../errorHandler';
import { resolveFuelOperationBases } from './resolveFuelOperationBases';
import {
  IFuelDistributorQuote,
  IProductAnpResponse,
  IWeeklyFuelVariationQuote,
  IWeeklyFuelVariationAggregated,
  IAvailableQuoteProduct,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IOperationBase,
} from './types';
export { useFetchQuotes } from './fetchQuotes';

/**
 * Serviços específicos para Cotações de Distribuidores
 */

export const QUOTES_FUEL_DISTRIBUTOR_BASE = '/quotes-fuel-distributor';

/** Endpoint process — confirmar versionamento no backend antes de alterar. */
export const QUOTES_FUEL_DISTRIBUTOR_PROCESS_PATH = '/quotes-fuel-distributor/process';

const FUEL_DISTRIBUTORS_BASE = '/fuel-distributors';
const FUEL_OPERATIONAL_BASES_BASE = '/fuel-operational-bases';
const PRODUCTS_ANP_BASE = '/products_anp';

// Obter todas as cotações de distribuidores
export const getFuelDistributorQuotes = async (): Promise<IFuelDistributorQuote[]> => {
  try {
    const response = await api.get(QUOTES_FUEL_DISTRIBUTOR_BASE);
    return response.data;
  } catch (error) {
    console.error('Erro detalhado ao buscar cotações:', error);
    throw handleApiError(error);
  }
};

// Criar uma nova cotação de distribuidor
export const createFuelDistributorQuote = async (
  quote: IFuelDistributorQuote
): Promise<IFuelDistributorQuote> => {
  try {
    const response = await api.post(QUOTES_FUEL_DISTRIBUTOR_BASE, quote);
    return response.data;
  } catch (error: any) {
    console.error('Erro detalhado ao criar cotação:', error);

    if (error.response) {
      console.error('Status do erro:', error.response.status);
      console.error('Dados do erro:', error.response.data);
      const err = new Error(
        `Erro ao criar cotação: ${error.response.data?.message || 'Erro de validação'}`
      ) as Error & { response?: typeof error.response };
      err.response = error.response;
      throw err;
    }

    throw handleApiError(error);
  }
};

// Atualizar uma cotação de distribuidor existente
export const updateFuelDistributorQuote = async (
  id: string,
  quote: IFuelDistributorQuote
): Promise<IFuelDistributorQuote> => {
  try {
    const response = await api.patch(`${QUOTES_FUEL_DISTRIBUTOR_BASE}/${id}`, quote);
    return response.data;
  } catch (error: any) {
    console.error('Erro detalhado ao atualizar cotação:', error);

    if (error.response) {
      console.error('Status do erro:', error.response.status);
      console.error('Dados do erro:', error.response.data);
      const err = new Error(
        `Erro ao atualizar cotação: ${error.response.data?.message || 'Erro de validação'}`
      ) as Error & { response?: typeof error.response };
      err.response = error.response;
      throw err;
    }

    throw handleApiError(error);
  }
};

// Excluir uma cotação de distribuidor
export const deleteFuelDistributorQuote = async (id: string): Promise<void> => {
  try {
    await api.delete(`${QUOTES_FUEL_DISTRIBUTOR_BASE}/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Obter um produto específico da ANP pelo ID
export const getProductAnp = async (id: string): Promise<IProductAnpResponse> => {
  try {
    const response = await api.get(`${PRODUCTS_ANP_BASE}/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Obter todas as distribuidoras de combustíveis com suas bases
export const getFuelDistributors = async () => {
  try {
    // Faz a requisição para obter os distribuidores completos, incluindo suas bases de operação
    const response = await api.get(`${FUEL_DISTRIBUTORS_BASE}?includes=fuelOperationBases`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Erro ao carregar distribuidoras:', error);
    throw handleApiError(error);
  }
};

// Obter bases de operação de uma distribuidora específica
export const getFuelOperationBases = async (distributorId?: string) => {
  try {
    // Verificar se ID do distribuidor foi fornecido
    if (distributorId) {
      // Buscar o distribuidor com suas bases
      const distributorResponse = await api.get(`${FUEL_DISTRIBUTORS_BASE}/${distributorId}`);
      const distributor = distributorResponse.data;
      const operationBases = resolveFuelOperationBases(distributor);

      if (operationBases.length > 0) {
        return operationBases;
      }

      console.warn('Distribuidor não possui bases de operação');
      return [];
    }

    // Se não foi fornecido ID, buscar todas as bases
    const response = await api.get(FUEL_OPERATIONAL_BASES_BASE);
    return response.data;
  } catch (error) {
    console.error('Erro ao carregar bases de operação:', error);
    return [];
  }
};

// Obter produtos por base de operação
export const getProductsByOperationBase = async (operationBaseId: string) => {
  try {
    try {
      // Usar o endpoint específico para bases de operação conforme documentação
      const response = await api.get(`${FUEL_OPERATIONAL_BASES_BASE}/${operationBaseId}`);

      // Verificar se a resposta contém produtos
      if (response.data && response.data.products && Array.isArray(response.data.products)) {
        return response.data.products;
      } else {
        console.warn('Base não possui produtos definidos na resposta');
        return [];
      }
    } catch (error) {
      console.error('Erro ao carregar dados da base de operação:', error);
      return [];
    }
  } catch (error) {
    console.error('Erro ao carregar produtos para base', operationBaseId, ':', error);
    // Retornando array vazio em caso de erro para evitar quebrar a interface
    return [];
  }
};

// Obter variação semanal de combustíveis
export const getWeeklyFuelVariation = async (
  quantity: number = 6,
  baseDate?: string,
  aggregationType?: 'min' | 'average'
): Promise<IWeeklyFuelVariationQuote[] | IWeeklyFuelVariationAggregated[]> => {
  try {
    const params = new URLSearchParams();
    params.append('quantity', quantity.toString());

    if (baseDate) {
      params.append('baseDate', baseDate);
    }

    if (aggregationType) {
      params.append('aggregationType', aggregationType);
    }

    const response = await api.get(
      `${QUOTES_FUEL_DISTRIBUTOR_BASE}/weekly-fuel-variation?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar variação semanal de combustíveis:', error);
    throw handleApiError(error);
  }
};

// Obter a data máxima disponível no banco de dados
export const getMaxDateFromDatabase = async (): Promise<string> => {
  try {
    const response = await api.get(`${QUOTES_FUEL_DISTRIBUTOR_BASE}/max-date`);
    return response.data.maxDate;
  } catch (error) {
    console.error('Erro ao buscar data máxima do banco:', error);
    return new Date().toISOString().split('T')[0]; // Fallback para data atual
  }
};

// Obter próxima data disponível para navegação
export const getNextAvailableDate = async (
  baseDate: string,
  direction: 'prev' | 'next',
  unit: 'day' | 'week' | 'month' = 'day',
  amount: number = 1
): Promise<string | null> => {
  try {
    const params = new URLSearchParams();
    params.append('baseDate', baseDate);
    params.append('direction', direction);
    params.append('unit', unit);
    params.append('amount', amount.toString());

    const response = await api.get(
      `${QUOTES_FUEL_DISTRIBUTOR_BASE}/next-available-date?${params.toString()}`
    );

    return response.data.hasNextDate ? response.data.nextDate : null;
  } catch (error) {
    console.error('Erro ao buscar próxima data disponível:', error);
    return null;
  }
};

export const getAvailableProductsWithoutOperationalBase = async (params: {
  customerId: string;
  quoteDate?: string;
  /** Quando informado, apenas produtos com cotação válida para essa base (GET available-products). */
  operationalBaseId?: string;
}): Promise<IAvailableQuoteProduct[]> => {
  try {
    const query = new URLSearchParams();
    query.append('customerId', params.customerId);
    if (params.quoteDate) query.append('quoteDate', params.quoteDate);
    if (params.operationalBaseId?.trim()) {
      query.append('operationalBaseId', params.operationalBaseId.trim());
    }
    const response = await api.get(
      `${QUOTES_FUEL_DISTRIBUTOR_BASE}/available-products?${query.toString()}`
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw handleApiError(error);
  }
};
